import { createKiddoAxios } from "./httpClient";

let apiPromise;
const getQuizApi = async () => {
  if (!apiPromise) {
    apiPromise = createKiddoAxios();
  }
  return apiPromise;
};

const parseKiddoResponse = (response) => {
  const { data } = response || {};
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (_err) {
      return { raw: data };
    }
  }
  return data;
};

export const QuizSubjectAll = async () => {
  const api = await getQuizApi();
  const response = await api.get("/read/QuizSubjectAll");
  return response.data;
};
export const QuizGradeAll = async () => {
  const api = await getQuizApi();
  const response = await api.get("/read/QuizGradeAll");
  return response.data;
};
export const QuizCategories = async () => {
  const api = await getQuizApi();
  const response = await api.get("/read/GetListQuizCategory");
  return response.data;
};
export const AllQuizList = async (params) => {
  try {
    const api = await getQuizApi();
    const response = await api.get("/read/GetListQuizQuestion", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching quiz list:", error);
    throw error;
  }
};
export const QuizPreview = async (params) => {
  const api = await getQuizApi();
  const response = await api.get("/read/GetQuizGenDetail", { params });
  return response.data;
};

// Toggle save/unsave quiz for a student
// Expects: { guid, studentId, isSaved }
export const SaveQuizToggle = async ({ guid, studentId, isSaved }) => {
  try {
    const form = new FormData();
    form.append("guid", String(guid));
    form.append("studentId", String(studentId));
    // backend accepts boolean as string
    form.append("isSaved", String(Boolean(isSaved)));

    const api = await getQuizApi();
    const response = await api.post("/Insert/SaveQuiz", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    console.error("Error calling SaveQuizToggle:", error);
    throw error;
  }
};

export const ArchiveQuizToggle = async ({ guid, isArchived, userId }) => {
  try {
    const form = new FormData();
    form.append("guid", String(guid));
    if (typeof userId !== "undefined" && userId !== null) {
      form.append("userId", String(userId));
    }
    form.append("isArchived", String(Boolean(isArchived)));

    const api = await getQuizApi();
    const response = await api.post("/insert/ArchiveQuiz", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    console.error("Error calling ArchiveQuizToggle:", error);
    throw error;
  }
};

export const GetArchivedQuizzes = async ({
  userId,
  page = 1,
  pageSize = 10,
} = {}) => {
  if (!userId) throw new Error("userId is required to fetch archived quizzes");
  const params = { userId, page, pageSize };
  const api = await getQuizApi();
  const response = await api.get("/read/GetArchivedQuizzes", { params });
  return response.data;
};

// Get saved quizzes list by student
export const GetSavedQuizzes = async ({ studentId, page = 1, pageSize = 10 }) => {
  const params = { studentId, page, pageSize };
  const api = await getQuizApi();
  const response = await api.get("/read/GetSavedQuizzes", { params });
  return response.data;
};

// Check daily limit for a student
export const CheckDailyLimitByStudent = async (studentId) => {
  const api = await getQuizApi();
  const response = await api.get("/read/checkDailyLimitByStudent", { params: { studentId } });
  return response.data; // expected { limitReached: boolean, answeredToday: number, limit: number }
};

export const GetQuizReview = async ({ startId, wrongOnly = false, Key } = {}) => {
  if (!startId) throw new Error("startId is required");
  const params = { startId, wrongOnly };
  if (Key) params.Key = Key; // opsional kalau perlu API key

  const api = await getQuizApi();
  const response = await api.get("/read/GetQuizReview", { params });
  return response.data; // shape sama seperti contoh kamu (questions, totalQuestions, dll)
};

export const VerifyQuizReviewPin = async ({ guid, pin }) => {
  if (!guid) throw new Error("guid is required to verify review PIN");

  const formData = new FormData();
  formData.append("guid", guid);
  formData.append("pin", typeof pin === "string" ? pin : String(pin ?? ""));

  const api = await getQuizApi();
  const response = await api.post("/read/VerifyQuizReviewPin", formData);
  const { data } = response;

  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return { raw: data };
    }
  }

  return data;
};

export const UpdateQuizCoverImage = async ({ quizGuid, userId, coverImage }) => {
  if (!quizGuid) throw new Error("quizGuid is required to update cover image");
  const form = new FormData();
  form.append("quizGuid", String(quizGuid));
  if (typeof userId !== "undefined" && userId !== null) {
    form.append("userId", String(userId));
  }
  if (coverImage) {
    form.append("coverImage", coverImage);
  }

  const api = await getQuizApi();
  const response = await api.post("/insert/UpdateQuizCoverImage", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return parseKiddoResponse(response);
};

export const CreateTutorQuiz = async (payload = {}) => {
  const api = await getQuizApi();
  const response = await api.post("/insert/CreateTutorQuiz", payload);
  return parseKiddoResponse(response);
};

const buildQuestionFormData = (payload = {}) => {
  const form = new FormData();
  const entries = {
    quizGuid: payload.quizGuid,
    userId: payload.userId,
    question: payload.question,
    choiceA: payload.choiceA,
    choiceB: payload.choiceB,
    choiceC: payload.choiceC,
    choiceD: payload.choiceD,
    choiceE: payload.choiceE,
    choiceF: payload.choiceF,
    choiceG: payload.choiceG,
    correctChoice: payload.correctChoice,
    isMultipleAnswer: String(Boolean(payload.isMultipleAnswer)),
  };

  Object.entries(entries).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, value);
  });

  const isFile =
    (typeof File !== "undefined" && payload.attachment instanceof File) ||
    (typeof Blob !== "undefined" && payload.attachment instanceof Blob);

  if (isFile) {
    form.append("attachment", payload.attachment);
  }

  return form;
};

export const AddTutorQuizQuestion = async (payload = {}) => {
  const api = await getQuizApi();
  const formData = buildQuestionFormData(payload);
  const response = await api.post("/insert/AddTutorQuizQuestion", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return parseKiddoResponse(response);
};

export const GenerateTutorQuizFromSelection = async (payload = {}) => {
  const api = await getQuizApi();
  const response = await api.post(
    "/insert/GenerateTutorQuizFromSelection",
    payload
  );
  return parseKiddoResponse(response);
};


